import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Upload, X, FileText, CheckCircle, Clock, AlertCircle, Sparkles, Zap, Shield, BookOpen, Trash2, Loader, Copy } from 'lucide-react';
import { toast } from 'react-toastify';
import { RootState } from '../store/reducers';
import { uploadDecisionsRequest, pollHistoriquesUpdate } from '../store/decisions/actions';
import { UploadResult, PipelineStep } from '../store/decisions/reducer';
import { fetchHistoriques } from '../store/decisions/service';
import './Numerisation.css';

interface LocalFile {
  id: number;
  file: File;
  name: string;
  size: number;
}

// Pipeline expected steps in order
const PIPELINE_STEPS = ['upload', 'extraction_api', 'anonymisation', 'resume_genere'];
const PIPELINE_LABELS: Record<string, string> = {
  upload: 'Upload',
  extraction_api: 'OCR',
  anonymisation: 'Anonymisation',
  resume_genere: 'Résumé IA',
};
const FINAL_STATUT = 'a_verifier';

const Numerisation: React.FC = () => {
  const dispatch = useDispatch();
  const { isUploading, uploadResults, pipelineSteps } = useSelector(
    (state: RootState) => state.decisions
  );

  const [localFiles, setLocalFiles] = useState<LocalFile[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [pipelinePolling, setPipelinePolling] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Track decisions that are "finished" (done or doublon) so we skip them in polling
  const finishedRef = useRef<Set<string>>(new Set());

  const startPolling = useCallback((decisionIds: string[]) => {
    if (decisionIds.length === 0) return;
    setPipelinePolling(true);
    finishedRef.current = new Set();

    // Poll every 60 seconds
    pollingRef.current = setInterval(async () => {
      let allDone = true;

      for (const decId of decisionIds) {
        // Skip already finished decisions
        if (finishedRef.current.has(decId)) continue;

        try {
          const response = await fetchHistoriques(decId);
          const items = response.data.results || response.data || [];
          dispatch(pollHistoriquesUpdate(decId, items));

          // Check for doublon_detecte
          const hasDoublon = items.some((s: any) => s.action === 'doublon_detecte');
          if (hasDoublon) {
            finishedRef.current.add(decId);
            const doublonStep = items.find((s: any) => s.action === 'doublon_detecte');
            const errMsg = doublonStep?.details?.error || 'Doublon détecté';
            toast.warning(`Doublon : ${errMsg}`, { autoClose: 8000 });
            continue;
          }

          // Check if pipeline is complete (last step has statut_apres = a_verifier)
          const lastStep = items[items.length - 1];
          if (lastStep && lastStep.statut_apres === FINAL_STATUT) {
            finishedRef.current.add(decId);
          } else {
            allDone = false;
          }
        } catch (e: any) {
          // 404 means the decision was auto-deleted (doublon cleanup)
          if (e?.response?.status === 404) {
            finishedRef.current.add(decId);
            // Mark as deleted doublon in pipeline steps
            dispatch(pollHistoriquesUpdate(decId, [
              { action: 'upload', statut_avant: null, statut_apres: 'brouillon', created_at: new Date().toISOString() },
              { action: 'doublon_detecte', acteur_type: 'systeme', statut_avant: 'brouillon', statut_apres: 'brouillon', details: { error: 'Décision supprimée (doublon)' }, created_at: new Date().toISOString() },
            ]));
          } else {
            allDone = false;
          }
        }
      }

      if (allDone) {
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = null;
        setPipelinePolling(false);

        const hasAnyDoublon = decisionIds.some(id => {
          const steps = (pipelineSteps[id] || []);
          return steps.some(s => s.action === 'doublon_detecte');
        });

        if (!hasAnyDoublon) {
          toast.success('Pipeline terminé ! Toutes les décisions sont prêtes à vérifier.');
        }
      }
    }, 30000); // 30 secondes
  }, [dispatch, pipelineSteps]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const addFiles = (newFiles: File[]) => {
    const validFiles = newFiles.filter(f => {
      const ext = f.name.toLowerCase().split('.').pop();
      if (!['pdf', 'doc', 'docx'].includes(ext || '')) {
        toast.error(`"${f.name}" : format non supporté. PDF, DOC, DOCX uniquement.`);
        return false;
      }
      if (f.size > 20 * 1024 * 1024) {
        toast.error(`"${f.name}" : taille trop grande (max 20 Mo).`);
        return false;
      }
      return true;
    });

    const items: LocalFile[] = validFiles.map((f, i) => ({
      id: Date.now() + i,
      file: f,
      name: f.name,
      size: f.size,
    }));
    setLocalFiles(prev => [...prev, ...items]);
    setUploadDone(false);
  };

  const removeFile = (id: number) => {
    setLocalFiles(prev => prev.filter(f => f.id !== id));
  };

  const clearAll = () => {
    if (!isUploading && !pipelinePolling) {
      setLocalFiles([]);
      setUploadDone(false);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      setPipelinePolling(false);
    }
  };

  const formatSize = (bytes: number): string => {
    if (bytes >= 1000000) return (bytes / 1000000).toFixed(2) + ' Mo';
    return (bytes / 1000).toFixed(0) + ' Ko';
  };

  const totalSize = localFiles.reduce((acc, f) => acc + f.size, 0);

  const handleLaunchClick = () => {
    if (localFiles.length > 0) {
      setShowModal(true);
    }
  };

  const startUpload = () => {
    setShowModal(false);
    const files = localFiles.map(lf => lf.file);
    dispatch(uploadDecisionsRequest(files, (results: UploadResult[]) => {
      setUploadDone(true);

      // Start polling for successful uploads that have decision_id
      const successIds = results
        .filter(r => r.success && r.decision_id)
        .map(r => r.decision_id!);

      if (successIds.length > 0) {
        startPolling(successIds);
      }
    }));
  };

  // Get pipeline progress for a decision
  const getPipelineProgress = (decisionId: string): {
    completedSteps: string[];
    currentStep: string | null;
    isDone: boolean;
    isDoublon: boolean;
    doublonMessage: string | null;
  } => {
    const steps = pipelineSteps[decisionId] || [];
    const completedActions = steps.map(s => s.action);

    // Check for doublon
    const doublonStep = steps.find(s => s.action === 'doublon_detecte');
    if (doublonStep) {
      const completedSteps = PIPELINE_STEPS.filter(ps => completedActions.includes(ps));
      return {
        completedSteps,
        currentStep: null,
        isDone: false,
        isDoublon: true,
        doublonMessage: doublonStep.details?.error || 'Doublon détecté',
      };
    }

    const completedSteps = PIPELINE_STEPS.filter(ps => completedActions.includes(ps));
    const lastStep = steps[steps.length - 1];
    const isDone = lastStep?.statut_apres === FINAL_STATUT;
    const currentStep = isDone ? null : PIPELINE_STEPS.find(ps => !completedActions.includes(ps)) || null;

    return { completedSteps, currentStep, isDone, isDoublon: false, doublonMessage: null };
  };

  const successResults = uploadResults.filter(r => r.success);
  const errorResults = uploadResults.filter(r => !r.success);
  const doublonResults = successResults.filter(r => {
    if (!r.decision_id) return false;
    const steps = pipelineSteps[r.decision_id] || [];
    return steps.some(s => s.action === 'doublon_detecte');
  });
  const isProcessing = isUploading || pipelinePolling;

  return (
    <div className="numerisation">
      {/* Drop zone */}
      <div
        className={`num-drop-zone ${isProcessing ? 'num-drop-zone-disabled' : ''}`}
        onDrop={isProcessing ? undefined : handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => !isProcessing && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={isProcessing}
        />
        <div className="num-drop-icon">
          <Upload size={28} />
        </div>
        <p className="num-drop-title">Glissez vos décisions ou cliquez pour parcourir</p>
        <p className="num-drop-info">PDF, DOC, DOCX uniquement · Max 20 Mo par fichier · Jusqu'à 50 fichiers par lot</p>
        <div className="num-drop-tags">
          <span className="num-tag"><Sparkles size={12} /> OCR avancé</span>
          <span className="num-tag"><Shield size={12} /> Anonymisation IA</span>
          <span className="num-tag"><BookOpen size={12} /> Résumé automatique</span>
        </div>
      </div>

      {localFiles.length > 0 && (
        <>
          {/* Action bar */}
          <div className="num-action-bar">
            <div className="num-action-info">
              <span>Fichiers <strong className="num-orange">{localFiles.length}</strong></span>
              <span className="num-sep">|</span>
              <span>Volume <strong>{formatSize(totalSize)}</strong></span>
              {isProcessing && (
                <>
                  <span className="num-sep">|</span>
                  <span className="num-uploading-label">
                    <Loader size={14} className="num-spin-inline" />
                    {isUploading ? 'Upload en cours...' : 'Pipeline en cours...'}
                  </span>
                </>
              )}
            </div>
            <div className="num-action-btns">
              <button className="num-btn-outline" onClick={clearAll} disabled={isProcessing}>
                <Trash2 size={14} /> Tout retirer
              </button>
              <button
                className="num-btn-launch"
                onClick={handleLaunchClick}
                disabled={isProcessing || localFiles.length === 0 || uploadDone}
              >
                {isUploading ? (
                  <><Loader size={16} className="num-spin-inline" /> Envoi en cours...</>
                ) : pipelinePolling ? (
                  <><Loader size={16} className="num-spin-inline" /> Pipeline en cours...</>
                ) : uploadDone && !pipelinePolling ? (
                  <><CheckCircle size={16} /> Traitement terminé</>
                ) : (
                  <><Zap size={16} /> Lancer le traitement ({localFiles.length})</>
                )}
              </button>
            </div>
          </div>

          {/* Pipeline progress per file (after upload) */}
          {uploadDone && successResults.length > 0 && (
            <div className="num-pipeline">
              <div className="num-pipeline-header">
                <div>
                  <h3 className="num-batch-title">Pipeline automatique</h3>
                  <p className="num-batch-sub">Upload → OCR → Anonymisation → Résumé IA → Prêt</p>
                </div>
              </div>
              {successResults.map((result, idx) => {
                const decId = result.decision_id;
                if (!decId) return null;
                const progress = getPipelineProgress(decId);
                const totalSteps = PIPELINE_STEPS.length;
                const doneCount = progress.completedSteps.length;
                const pct = progress.isDone ? 100 : Math.round((doneCount / totalSteps) * 100);

                // Doublon card
                if (progress.isDoublon) {
                  return (
                    <div key={decId} className="num-pipe-card num-pipe-card-doublon">
                      <div className="num-pipe-card-top">
                        <div className="num-pipe-card-left">
                          <div className="num-pipe-icon num-pipe-icon-doublon">
                            <Copy size={20} />
                          </div>
                          <div>
                            <span className="num-pipe-name">{result.fichier || `Décision ${idx + 1}`}</span>
                            <span className="num-pipe-meta num-pipe-meta-doublon">Doublon détecté</span>
                          </div>
                        </div>
                        <div className="num-pipe-card-right">
                          <span className="num-status-badge num-status-doublon">
                            <Copy size={12} /> Doublon
                          </span>
                        </div>
                      </div>
                      <div className="num-doublon-message">
                        <AlertCircle size={14} />
                        <span>{progress.doublonMessage}</span>
                      </div>
                      <div className="num-doublon-info">
                        Cette décision sera automatiquement supprimée.
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={decId} className={`num-pipe-card ${progress.isDone ? 'num-pipe-card-done' : ''}`}>
                    <div className="num-pipe-card-top">
                      <div className="num-pipe-card-left">
                        <div className={`num-pipe-icon ${progress.isDone ? 'num-pipe-icon-done' : ''}`}>
                          <FileText size={20} />
                        </div>
                        <div>
                          <span className="num-pipe-name">{result.fichier || `Décision ${idx + 1}`}</span>
                          <span className="num-pipe-meta">
                            {progress.isDone
                              ? 'Prêt à vérifier'
                              : progress.currentStep
                                ? `En cours : ${PIPELINE_LABELS[progress.currentStep] || progress.currentStep}`
                                : 'En attente...'
                            }
                          </span>
                        </div>
                      </div>
                      <div className="num-pipe-card-right">
                        <span className={`num-status-badge ${progress.isDone ? 'num-status-done' : 'num-status-active'}`}>
                          {progress.isDone ? <><CheckCircle size={12} /> Terminé</> : <><Clock size={12} /> {pct}%</>}
                        </span>
                      </div>
                    </div>

                    {/* Segmented progress bar */}
                    <div className="num-pipe-bar">
                      {PIPELINE_STEPS.map((step) => {
                        const isStepDone = progress.completedSteps.includes(step);
                        const isCurrent = progress.currentStep === step;
                        return (
                          <div key={step} className="num-pipe-segment">
                            <div
                              className={`num-pipe-segment-fill ${isStepDone ? 'num-pipe-seg-done' : isCurrent ? 'num-pipe-seg-active' : ''}`}
                              style={{ width: isStepDone ? '100%' : isCurrent ? '50%' : '0%' }}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="num-pipe-labels">
                      {PIPELINE_STEPS.map((step) => {
                        const isStepDone = progress.completedSteps.includes(step);
                        const isCurrent = progress.currentStep === step;
                        return (
                          <span
                            key={step}
                            className={`num-pipe-lbl ${isStepDone ? 'num-pipe-lbl-done' : isCurrent ? 'num-pipe-lbl-active' : ''}`}
                          >
                            {PIPELINE_LABELS[step]}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* File list */}
          <div className="num-file-section">
            <div className="num-file-label">
              FICHIERS ({localFiles.length})
              {uploadDone && (
                <>
                  {` — ${successResults.length} envoyé(s), ${errorResults.length} erreur(s)`}
                  {doublonResults.length > 0 && `, ${doublonResults.length} doublon(s)`}
                </>
              )}
            </div>
            {localFiles.map(file => {
              const result = uploadDone ? uploadResults.find(r => r.fichier === file.name) : undefined;
              const isDone = result?.success === true;
              const isError = result?.success === false;
              // Check pipeline status for this file's decision
              const decId = result?.decision_id;
              const pipeProgress = decId ? getPipelineProgress(decId) : null;
              const isPipelineDone = pipeProgress?.isDone === true;
              const isDoublon = pipeProgress?.isDoublon === true;

              return (
                <div key={file.id} className={`num-file-row ${isPipelineDone ? 'num-file-row-done' : ''} ${isError ? 'num-file-row-error' : ''} ${isDoublon ? 'num-file-row-doublon' : ''}`}>
                  <div className="num-file-icon-wrap">
                    {isDoublon ? <Copy size={20} /> : <FileText size={20} />}
                  </div>
                  <div className="num-file-info">
                    <span className="num-file-name">{file.name}</span>
                    <span className="num-file-size">{formatSize(file.size)}</span>
                  </div>
                  <div className="num-file-bar-wrap">
                    {isUploading && (
                      <div className="num-file-bar">
                        <div className="num-file-bar-fill num-file-bar-indeterminate" />
                      </div>
                    )}
                    {isDone && pipeProgress && !isPipelineDone && !isDoublon && (
                      <div className="num-file-bar">
                        <div
                          className="num-file-bar-fill"
                          style={{ width: `${(pipeProgress.completedSteps.length / PIPELINE_STEPS.length) * 100}%` }}
                        />
                      </div>
                    )}
                    {isPipelineDone && (
                      <div className="num-file-bar num-file-bar-done">
                        <div className="num-file-bar-fill" style={{ width: '100%' }} />
                      </div>
                    )}
                  </div>
                  <span className={`num-status-badge ${
                    isDoublon ? 'num-status-doublon' :
                    isPipelineDone ? 'num-status-done' :
                    isError ? 'num-status-error' :
                    (isDone && pipelinePolling) ? 'num-status-active' :
                    isUploading ? 'num-status-active' : 'num-status-pending'
                  }`}>
                    {isDoublon && <><Copy size={12} /> Doublon</>}
                    {!isDoublon && isPipelineDone && <><CheckCircle size={12} /> Prêt</>}
                    {!isDoublon && isError && <><AlertCircle size={12} /> Erreur</>}
                    {!isDoublon && isDone && pipelinePolling && !isPipelineDone && <><Clock size={12} /> Pipeline...</>}
                    {!isDoublon && isUploading && <><Clock size={12} /> Upload...</>}
                    {!isDoublon && !isDone && !isError && !isUploading && !isPipelineDone && <><Clock size={12} /> En attente</>}
                  </span>
                  {isError && result?.error && (
                    <span className="num-file-error-msg" title={result.error}>
                      {result.error.length > 30 ? result.error.substring(0, 30) + '...' : result.error}
                    </span>
                  )}
                  {isDoublon && pipeProgress?.doublonMessage && (
                    <span className="num-file-error-msg num-file-doublon-msg" title={pipeProgress.doublonMessage}>
                      {pipeProgress.doublonMessage.length > 50 ? pipeProgress.doublonMessage.substring(0, 50) + '...' : pipeProgress.doublonMessage}
                    </span>
                  )}
                  {!isProcessing && (
                    <button className="num-file-remove" onClick={() => removeFile(file.id)}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Global summary after all pipeline done */}
          {uploadDone && !pipelinePolling && uploadResults.length > 0 && (
            <div className="num-batch">
              <div className="num-batch-header">
                <div>
                  <h3 className="num-batch-title">Résultat final</h3>
                  <p className="num-batch-sub">
                    {uploadResults.length} fichier(s) traité(s)
                  </p>
                </div>
                <span className={`num-batch-pct ${errorResults.length === 0 ? 'num-batch-pct-done' : ''}`}>
                  {uploadResults.length > 0 ? Math.round((successResults.length / uploadResults.length) * 100) : 0}%
                </span>
              </div>
              <div className="num-batch-bar">
                <div
                  className="num-batch-bar-fill"
                  style={{
                    width: `${uploadResults.length > 0 ? (successResults.length / uploadResults.length) * 100 : 0}%`,
                    background: errorResults.length === 0 ? '#059669' : '#E85D04',
                  }}
                />
              </div>
              <div className="num-batch-stats">
                <div className="num-bstat">
                  <div className="num-bstat-dot num-bstat-dot-done"><CheckCircle size={10} /></div>
                  <span className="num-bstat-label">SUCCÈS</span>
                  <span className="num-bstat-val">{successResults.length - doublonResults.length}</span>
                </div>
                {doublonResults.length > 0 && (
                  <div className="num-bstat">
                    <div className="num-bstat-dot num-bstat-dot-doublon"><Copy size={10} /></div>
                    <span className="num-bstat-label">DOUBLONS</span>
                    <span className="num-bstat-val">{doublonResults.length}</span>
                  </div>
                )}
                <div className="num-bstat">
                  <div className="num-bstat-dot num-bstat-dot-error"><AlertCircle size={10} /></div>
                  <span className="num-bstat-label">ERREURS</span>
                  <span className="num-bstat-val">{errorResults.length}</span>
                </div>
                <div className="num-bstat">
                  <div className="num-bstat-dot num-bstat-dot-pending"><FileText size={10} /></div>
                  <span className="num-bstat-label">TOTAL</span>
                  <span className="num-bstat-val">{uploadResults.length}</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {localFiles.length === 0 && (
        <div className="num-empty">
          <div className="num-empty-header">
            <h3 className="num-batch-title">File de traitement (0)</h3>
            <p className="num-batch-sub">Suivez la progression OCR → Anonymisation → Résumé</p>
          </div>
          <div className="num-empty-body">
            <Sparkles size={32} className="num-empty-icon" />
            <p>Aucun fichier en file d'attente. Importez des décisions pour commencer.</p>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {showModal && (
        <div className="num-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="num-modal" onClick={e => e.stopPropagation()}>
            <div className="num-modal-head">
              <div className="num-modal-title-row">
                <div className="num-modal-icon">
                  <Upload size={20} />
                </div>
                <div>
                  <h3 className="num-modal-title">Confirmer le traitement par lot</h3>
                  <p className="num-modal-subtitle">Vérifiez le récapitulatif avant de lancer le traitement.</p>
                </div>
              </div>
              <button className="num-modal-close" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="num-modal-body">
              <div className="num-modal-summary">
                <div className="num-modal-row">
                  <span>Fichiers à traiter</span>
                  <strong className="num-orange">{localFiles.length}</strong>
                </div>
                <div className="num-modal-row">
                  <span>Volume total</span>
                  <strong>{formatSize(totalSize)}</strong>
                </div>
                <div className="num-modal-row">
                  <span>Pipeline</span>
                  <strong>OCR → Anonymisation → Résumé</strong>
                </div>
              </div>
              <div className="num-modal-files">
                {localFiles.map(f => (
                  <div key={f.id} className="num-modal-file">
                    <span>{f.name}</span>
                    <span className="num-modal-file-size">{formatSize(f.size)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="num-modal-foot">
              <button className="num-btn-cancel" onClick={() => setShowModal(false)}>
                Annuler
              </button>
              <button className="num-btn-confirm" onClick={startUpload}>
                <Zap size={14} /> Confirmer et lancer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Numerisation;
